import React, { useState } from "react";
import {
  Card,
  Box,
  Typography,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { useTranslation } from "react-i18next";
import Matrix6 from "../../../img/logos/Matrix6.png";
import IncotermsTable from "./IncotermsTable";
import { INCOTERMS_ACCENT } from "./incotermsConstants";

const IncotermsCard = () => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const theme = useTheme();

  const handleToggle = () => setOpen((prev) => !prev);

  const closedCardSx = {
    p: 3,
    height: "9em",
    borderRadius: 3,
    cursor: "pointer",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    border: `1px solid ${alpha(INCOTERMS_ACCENT, 0.12)}`,
    background: `linear-gradient(135deg, ${alpha(INCOTERMS_ACCENT, 0.06)} 0%, ${alpha(
      theme.palette.common.white,
      0.98,
    )} 100%)`,
    boxShadow: `0 8px 22px ${alpha(theme.palette.common.black, 0.07)}`,
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: `0 14px 32px ${alpha(INCOTERMS_ACCENT, 0.14)}`,
    },
  };

  return (
    <Box sx={{ position: "relative" }}>
      {!open && (
        <Card onClick={handleToggle} elevation={0} sx={closedCardSx}>
          <Box
            component="img"
            src={Matrix6}
            alt="TON Logo"
            sx={{ width: 40, height: 40, mr: 1 }}
          />
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" }, lineHeight: 1.3 }}
          >
            {t("incoterms.title")}
          </Typography>
          <LocalShippingIcon sx={{ color: INCOTERMS_ACCENT, ml: 1 }} />
        </Card>
      )}

      {open && (
        <Card
          elevation={0}
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "98vw", sm: "96vw", md: "94vw" },
            maxHeight: "94vh",
            overflowY: "auto",
            zIndex: 2000,
            borderRadius: 4,
            border: `1px solid ${alpha(INCOTERMS_ACCENT, 0.1)}`,
            boxShadow: `0 24px 64px ${alpha(theme.palette.common.black, 0.22)}`,
            background: "#eef2f7",
            p: 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              px: { xs: 2, md: 3 },
              py: 1.75,
              background: `linear-gradient(135deg, ${alpha(INCOTERMS_ACCENT, 0.1)} 0%, ${alpha(
                theme.palette.common.white,
                0.94,
              )} 72%)`,
              borderBottom: `1px solid ${alpha(INCOTERMS_ACCENT, 0.1)}`,
              backdropFilter: "blur(14px)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
              <Box
                component="img"
                src={Matrix6}
                alt="TON Logo"
                sx={{ width: 42, height: 42, flexShrink: 0 }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="overline"
                  sx={{
                    display: "block",
                    fontWeight: 800,
                    letterSpacing: 0.8,
                    lineHeight: 1,
                    color: alpha(INCOTERMS_ACCENT, 0.7),
                    fontSize: "0.62rem",
                  }}
                >
                  NML · Conocimiento
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 900,
                    lineHeight: 1.2,
                    fontSize: { xs: "0.95rem", md: "1.05rem" },
                  }}
                >
                  {t("incoterms.title")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("incoterms.subtitle")}
                </Typography>
              </Box>
            </Box>

            <IconButton
              onClick={handleToggle}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.grey[500], 0.1),
                flexShrink: 0,
                "&:hover": { bgcolor: alpha(theme.palette.grey[500], 0.18) },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Paper>

          <Box sx={{ py: { xs: 1.5, md: 2 }, px: { xs: 1, md: 2 } }}>
            <IncotermsTable />
          </Box>
        </Card>
      )}
    </Box>
  );
};

export default IncotermsCard;
